import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, IconButton, Modal, makeStyles, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Sidebar from './AIAssistanceModalSidebar';
import ChatArea from './AIAssistanceChatArea';
import { DraggableBox } from '../../atoms/DraggableBox/DraggableBox';
import MinimizeIcon from '@material-ui/icons/Remove'; // Or KeyboardArrowDown
import OpenInFullIcon from '@material-ui/icons/AspectRatio';
import { getAiAssistant, getAiAssistantAutoProceed, getAiAssistantLoading } from '../../../store/global/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { closeAiAssistantModal, invokeGetService, updateAiAssistant } from '../../../store/global/actions';
import ResourcePane from '../AIResources/index'

const useStyles = makeStyles((theme) => ({
  /* --- Minimized Style --- */
  minimizedBox: {
    width: 240,
    color: '#000',
    padding: '5px 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  pulseDot: {
    width: 8,
    height: 8,
    backgroundColor: '#10b981', // Your brand Yellow
    borderRadius: '50%',
    marginRight: theme.spacing(1.5),
    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
  },
  animatedPulseDot: {
    animation: '$pulseGreen 2s infinite',
  },
  reviewPulseDot: {
    backgroundColor: '#f59e0b',
    animation: '$pulseAmber 1.2s infinite',
  },

  '@keyframes pulseGreen': {
    '0%': { boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)' },
    '70%': { boxShadow: '0 0 0 10px rgba(255, 234, 0, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 234, 0, 0)' },
  },

  '@keyframes pulseAmber': {
    '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.7)' },
    '70%': { boxShadow: '0 0 0 8px rgba(245, 158, 11, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
  },

  /* --- Expanded Styles --- */
  modalContent: {
    width: 1000,
    height: '82vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: theme.shadows[24],
  },
  draggableContainer: {
    zIndex: "1301 !important"
  }
}));


const stepOrder = {
  system_model: 1,
  database_profile: 2,
  view: 3,
  transaction: 4,
  simulation: 5,
};

const stepLabel = {
  system_model: 'System Model',
  database_profile: 'Database Model',
  view: 'Views',
  transaction: 'Transactions',
  simulation: 'Simulations',
};

const AIAssistanceModal = () => {
  const { aiAssistantOpen, loading, isMinimized } = useSelector(getAiAssistant);
  const classes = useStyles();
  const dispatch = useDispatch();
  const autoProceed = useSelector(getAiAssistantAutoProceed);
  const isGenerationProgress = useSelector(getAiAssistantLoading);


  const [aiGeneratedItems, setAiGeneratedItems] = useState([]);
  const [activeStep, setActiveStep] = useState("system_model");
  const [summaryExist, setSummaryExist] = useState(false)
  const [waitingForAnswer, setWatingForAnswer] = useState(false)
  const [isAutoGenerate, setAutoGenerate] = useState(false)
    const [clusterletsList, setClusterletsList] = useState([]);

  const setIsMinimized = (val) => dispatch(updateAiAssistant({ isMinimized: val }));


  const handleClose = () => {
    dispatch(closeAiAssistantModal());
    setActiveStep("system_model");
  }

  useEffect(() => {
    if (!aiAssistantOpen && isMinimized) {
      setIsMinimized(false);
    }
  }, [aiAssistantOpen])

  const isAwaitingReview = (
    autoProceed === 'review' &&
    !isGenerationProgress &&
    aiGeneratedItems?.length > 0
  );

  const getLLMSummary = async () => {
    const response = await dispatch(invokeGetService('llmFileSummary', {}));
    setSummaryExist(response.status === "processed")
  }

  useEffect(() => {
    getLLMSummary();
  },[])


  return (
    <>
      {
        isMinimized && aiAssistantOpen &&
        <DraggableBox defaultPosition="bottom-left" className={classes.draggableContainer}>
          <Box className={classes.minimizedBox}>
            <Box display="flex" alignItems="center">
              <div className={`${classes.pulseDot} 
              ${isAwaitingReview ? classes.reviewPulseDot : loading ? classes.animatedPulseDot : ''}
              `} />
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle2" style={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  AI Agent Active
                </Typography>
                <Typography style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                  {isAwaitingReview
                    ? `Review Required — ${stepLabel[activeStep]} is ready`
                    : loading
                      ? `Generating ${stepLabel[activeStep]}`
                      : `Step ${stepOrder[activeStep]} of 5 · ${stepLabel[activeStep]}`
                  }
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsMinimized(false)}
              style={{ color: '#000', padding: 4 }}
            >
              <OpenInFullIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </DraggableBox>
      }
      <Modal
        open={aiAssistantOpen}
        disableScrollLock
        style={{
          display: isMinimized ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1301
        }}
      >
        <Paper elevation={24} style={{ width: '90%', maxWidth: 1000, height: '85vh', display: 'flex', flexDirection: 'column', borderRadius: 12, overflow: 'hidden', outline: 'none' }}>

          {/* Header */}
          <Box px={3} py={1.75} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #edf2f7">
            <Box display="flex" alignItems="center">
              {/* <div className={`${classes.pulseDot} ${loading ? classes.animatedPulseDot : ''}`} /> */}
              <Typography variant="h6" style={{ fontWeight: 800, color: '#1e1b4b', fontSize: '1.1rem' }}>
                Hyperblox AI Agent
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => setIsMinimized(true)} size="small" style={{ marginRight: 8 }}>
                <MinimizeIcon fontSize="small" />
              </IconButton>
              <Tooltip title={loading
                ? 'Generation in progress — please wait'
                : aiGeneratedItems?.length
                  ? 'Complete or discard the current generation first'
                  : ''}>
                <span>
                  <IconButton onClick={handleClose} disabled={loading || !!aiGeneratedItems?.length || waitingForAnswer} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Content */}
          <Box display="flex" flex={1} overflow="hidden">
            <Sidebar 
              setActiveStep={setActiveStep} 
              activeStep={activeStep} 
              aiGeneratedItems={aiGeneratedItems} 
              isSummaryExist={summaryExist}
              waitingForAnswer={waitingForAnswer}
            />
            {
              activeStep !== "resources" ? 
              <ChatArea
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                aiGeneratedItems={aiGeneratedItems}
                setAiGeneratedItems={setAiGeneratedItems}
                isAwaitingReview={isAwaitingReview}
                summaryExist={summaryExist}
                waitingForAnswer={waitingForAnswer}
                setWatingForAnswer={setWatingForAnswer}
                autoGenerate={isAutoGenerate}
                setAutoGenerate={setAutoGenerate}
                clusterletsList={clusterletsList}
                setClusterletsList={setClusterletsList}
              />
              : activeStep === "resources" ?
              <ResourcePane 
                setActiveStep={setActiveStep} 
                setSummaryExist={setSummaryExist}
                setAutoGenerate={setAutoGenerate}
                clusterletsList={clusterletsList}
              />
              :null
            }
          </Box>

        </Paper>
      </Modal>
    </>
  );
};

export default AIAssistanceModal;